import { useState } from "react";
import { Link } from "react-router";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  TableFooter,
  TablePagination,
  Tooltip,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { format } from "date-fns";
import { InvestmentApiResponse } from "../../types";
import routes from "../../../../routes";
import { EditNote } from "./components/EditNote.js";

export const investmentDateFormat = "MMM dd yyyy";
interface RowProps {
  investment: InvestmentApiResponse;
  fetchInvestmentDetails: () => Promise<void>;
}

function Row(props: RowProps) {
  const { investment } = props;
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const individualInvestments = (investment.individualInvestments || []).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const paginatedIndividualInvestments = individualInvestments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            data-testid="individualInvestments-table-button"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Link to={routes.solutions.view({ id: investment.solutionId })}>
            {investment.projectName}
          </Link>
        </TableCell>
        <TableCell align="left">{investment.unit}</TableCell>
        <TableCell align="left">{investment.totalInvested}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="individual investments">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: "150px",
                      }}
                    >
                      Created At
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: "120px",
                      }}
                    >
                      Unit
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: "75px",
                      }}
                    >
                      Quantity
                    </TableCell>
                    <TableCell align="left">Note</TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: "100px",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedIndividualInvestments.map(
                    (individualInvestment, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {format(
                            new Date(individualInvestment.createdAt),
                            "MMM dd yyyy",
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {individualInvestment.unit}
                        </TableCell>
                        <TableCell align="left">
                          {individualInvestment.quantity}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            maxWidth: "100px",
                          }}
                        >
                          <Tooltip title={individualInvestment.note}>
                            <Typography
                              noWrap
                              sx={{
                                display: "block",

                                textOverflow: "ellipsis",
                              }}
                            >
                              {individualInvestment.note}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            padding: 0,
                          }}
                        >
                          <EditNote
                            investment={individualInvestment}
                            projectName={investment.projectName}
                            refreshInvestmentList={async () => {
                              await props.fetchInvestmentDetails();
                              setOpen(() => true);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
                {individualInvestments.length > 10 && (
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[10]}
                        count={individualInvestments.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                      />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export type InvestmentFieldsProps = {
  investments: Array<InvestmentApiResponse>;
  loading: boolean;
  error: Error | null;
  fetchInvestmentDetails: () => Promise<void>;
};

function InvestmentInfoFields({
  investments,
  loading,
  error,
  fetchInvestmentDetails,
}: InvestmentFieldsProps) {
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  const hasInvestments = investments.length > 0;
  if (!hasInvestments) {
    return (
      <Typography sx={{ paddingBottom: 4 }} align="left" variant="h6">
        No Investments found
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} data-testid="investment-info-container">
        <Table sx={{ paddingBottom: 2 }} aria-label="collapssble table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Project Name</TableCell>
              <TableCell align="left">Unit</TableCell>
              <TableCell align="left">Total Invested</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {investments.map((investment: InvestmentApiResponse, i: number) => (
              <Row
                key={investment.projectName + i}
                investment={investment}
                fetchInvestmentDetails={fetchInvestmentDetails}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default InvestmentInfoFields;
